import React, { useEffect, useState } from 'react';

import { AssetAllocation } from '@ndvr/portfolio-api';

import { useSecurityAssetAllocation } from '@/cip/api/assetAllocation';
import {
    ASSET_CLASS_BREAKDOWN_CATEGORIES,
    formatRatio,
    getAssetAllocationCategoryLabel,
} from 'utils/asset-allocation-utils';
import { getCategoryColor } from 'views/household/OverviewTab/constants';

interface StrategyExposurePanelProps {
    assetAllocations: AssetAllocation[];
}

const DEBOUNCE_MS = 400;

export const StrategyExposurePanel: React.FC<StrategyExposurePanelProps> = ({ assetAllocations }) => {
    const [debounced, setDebounced] = useState(assetAllocations);

    useEffect(() => {
        const timer = setTimeout(() => setDebounced(assetAllocations), DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [assetAllocations]);

    const securityWeights = debounced
        .filter((asset) => asset.securityId && parseFloat(asset.weight) > 0)
        .map((asset) => ({ sId: asset.securityId, weight: parseFloat(asset.weight) / 100 }));

    const { data: exposure = [], isLoading } = useSecurityAssetAllocation(securityWeights);

    const holdingsCount = assetAllocations.length;
    const tlhCoveredCount = assetAllocations.filter((asset) => asset.replacementAssets.length > 0).length;

    const slices = ASSET_CLASS_BREAKDOWN_CATEGORIES.map((category) => ({
        category,
        weight: exposure.find((e) => e.category === category)?.weight ?? 0,
    }))
        .filter((slice) => slice.weight > 0)
        .sort((a, b) => b.weight - a.weight);

    return (
        <div className="border-cocoa-200 flex flex-col gap-3.5 rounded-lg border bg-white p-5">
            <div>
                <h4 className="text-cocoa-1000 text-base font-medium">Asset-class exposure</h4>
                <p className="text-cocoa-500 text-xs">Recomputed as weights change</p>
            </div>

            {holdingsCount === 0 ? (
                <p className="text-cocoa-500 text-sm">Add tickers to see exposure.</p>
            ) : (
                <>
                    <div className="bg-cocoa-0 flex h-5 overflow-hidden rounded">
                        {slices.map((slice) => (
                            <div
                                key={slice.category}
                                style={{
                                    width: `${slice.weight * 100}%`,
                                    backgroundColor: getCategoryColor(slice.category),
                                }}
                            />
                        ))}
                    </div>

                    <div className="flex flex-col gap-2">
                        {slices.map((slice) => (
                            <div key={slice.category} className="flex items-center gap-2">
                                <span
                                    className="h-2.5 w-2.5 shrink-0 rounded-sm"
                                    style={{ backgroundColor: getCategoryColor(slice.category) }}
                                />
                                <span className="text-cocoa-700 text-sm">
                                    {getAssetAllocationCategoryLabel(slice.category)}
                                </span>
                                <span className="text-cocoa-1000 ml-auto text-sm font-medium">
                                    {formatRatio(slice.weight)}
                                </span>
                            </div>
                        ))}
                        {isLoading && slices.length === 0 && <p className="text-cocoa-500 text-sm">Calculating...</p>}
                    </div>

                    <div className="border-cocoa-200/50 flex flex-col gap-1.5 border-t pt-3">
                        <div className="flex items-center gap-2 text-sm">
                            <span className="text-cocoa-500">Holdings</span>
                            <span className="text-cocoa-1000 ml-auto font-medium">{holdingsCount}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                            <span className="text-cocoa-500">TLH Replacements</span>
                            <span className="text-cocoa-1000 ml-auto font-medium">
                                {tlhCoveredCount} of {holdingsCount}
                            </span>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
